/**
 * Iter 57 — face-api.js wrapper for browser-side face detection + descriptor extraction.
 *
 * Usage:
 *   import { loadFaceModels, captureFaceDescriptor } from '../lib/faceRecognition';
 *   await loadFaceModels();
 *   const { descriptor, photoDataUrl } = await captureFaceDescriptor(videoElement);
 */
import * as faceapi from 'face-api.js';

// Resolve model URL for the current runtime:
//   • Capacitor native app → /api/face-models
//     Served via the backend API to bypass Cloudflare SPA routing.
//     The /face-models/ static path can be intercepted by Cloudflare's
//     catch-all SPA rule (returning index.html), so routing via /api/
//     guarantees binary delivery.
//   • Web / PWA → /face-models (served from the deployed static files)
const _resolveModelUrl = () => {
  try {
    const cap = window.Capacitor;
    if (cap && cap.isNativePlatform && cap.isNativePlatform()) {
      // Use REACT_APP_BACKEND_URL so it works for both production and preview
      const base = process.env.REACT_APP_BACKEND_URL || 'https://elloraepc.in';
      return `${base}/api/face-models`;
    }
  } catch { /* not native */ }
  return '/face-models';
};

const MODEL_URL = _resolveModelUrl();
let modelsLoaded = false;
let loadingPromise = null;

export const loadFaceModels = async () => {
  if (modelsLoaded) return true;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]);
    modelsLoaded = true;
    return true;
  })();
  return loadingPromise;
};

export const isModelsLoaded = () => modelsLoaded;

/**
 * Iter 63 — compute head yaw (turn left/right) from face-api landmarks (68 pts).
 *
 * Returns the yaw ratio:
 *   ≈  0    → looking straight ahead (front)
 *   > +0.12 → head turned to user's LEFT (camera sees right side of face)
 *   < -0.12 → head turned to user's RIGHT
 *
 * Algorithm: distance from nose tip to each cheekbone normalised by face width.
 * Robust to camera mirror because the metric is signed but consistent per side.
 */
export const computeHeadYaw = (landmarks) => {
  if (!landmarks?.positions) return null;
  const pts = landmarks.positions;
  // Outer face corners
  const leftCheek = pts[0];      // user's left (image right when mirrored)
  const rightCheek = pts[16];    // user's right
  const noseTip = pts[30];
  if (!leftCheek || !rightCheek || !noseTip) return null;
  const faceWidth = Math.abs(rightCheek.x - leftCheek.x) || 1;
  const noseMidOffset = noseTip.x - (leftCheek.x + rightCheek.x) / 2;
  // Normalise to -1..+1. Multiply 2/faceWidth so a nose at full-left cheek ≈ -1.
  const yawRatio = (2 * noseMidOffset) / faceWidth;
  return yawRatio;
};

/**
 * Iter 63 — classify the current pose given a yaw ratio.
 */
export const classifyPose = (yawRatio) => {
  if (yawRatio === null || yawRatio === undefined) return 'unknown';
  if (yawRatio > 0.18) return 'left';      // head turned to user's left
  if (yawRatio < -0.18) return 'right';
  if (Math.abs(yawRatio) < 0.08) return 'front';
  return 'transition';   // in between — ask user to turn more or recenter
};

/**
 * Detect a single face in the given <video> element and return its
 * 128-dim descriptor + a base64 thumbnail of the face crop.
 *
 * Returns null if no face / multiple faces detected.
 */
export const captureFaceDescriptor = async (videoEl, { forEnrollment = false } = {}) => {
  if (!modelsLoaded) await loadFaceModels();
  // ── Anti-spoofing: the capture source MUST be a live camera MediaStream.
  // Guards against pointing videoEl.srcObject at an image / synthetic source.
  const stream = videoEl && videoEl.srcObject;
  if (!(stream instanceof MediaStream)) {
    throw new Error('Capture source is not a live camera stream.');
  }
  if (!stream.getVideoTracks().some((t) => t.readyState === 'live')) {
    throw new Error('Capture source is not a live camera stream.');
  }
  // inputSize 224 (vs 320) is ~1.5–2× faster on mid-range phones with negligible
  // detection-quality loss for selfie-style captures. scoreThreshold 0.4 keeps recall.
  const detections = await faceapi
    .detectAllFaces(videoEl, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }))
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (detections.length === 0) return { error: 'No face detected. Look straight at the camera.' };
  if (detections.length > 1) return { error: 'Multiple faces detected. Only one person at a time, please.' };

  const det = detections[0];

  // ── Enrollment quality gates (stricter than live-punch to ensure good descriptor quality) ──
  if (forEnrollment) {
    const box = det.detection.box;
    const videoArea = (videoEl.videoWidth || 1) * (videoEl.videoHeight || 1);
    const facePct = (box.width * box.height) / videoArea;
    if (facePct < 0.08) {
      return { error: 'Move closer to the camera — face is too small in frame.' };
    }
    if (det.detection.score < 0.65) {
      return { error: 'Face not clearly visible — improve lighting or remove obstructions.' };
    }
    // Brightness uniformity check (harsh sunlight/shadow on one side)
    const result = _cropAndIndicators(videoEl, box, det.detection.score, Array.from(det.descriptor));
    if (result.screen_indicators?.brightness_cv > 0.5) {
      return { error: 'Uneven lighting detected — stand facing a light source, avoid harsh shadows.' };
    }
    return result;
  }

  // Crop + screen-indicators via a shared helper (reused by the liveness
  // final-frame fast path so we never run a second/third detection pass).
  return _cropAndIndicators(videoEl, det.detection.box, det.detection.score, Array.from(det.descriptor));
};

/**
 * Screen/display detection on the existing 200×200 face-crop canvas (no extra
 * capture). Returns { moire_score, brightness_cv } — contributing anti-spoofing
 * signals (the SERVER decides). A phone screen held to the camera shows:
 *  • a Moiré interference pattern → high-frequency oscillation in pixel diffs;
 *  • suspiciously UNIFORM backlight → low coefficient-of-variation across 5 points.
 * A real face has organic texture + non-uniform shadow/highlight distribution.
 */
const _computeScreenIndicators = (ctx) => {
  const S = 200;
  const full = ctx.getImageData(0, 0, S, S).data; // RGBA
  const grayAt = (x, y) => {
    const i = (y * S + x) * 4;
    return 0.299 * full[i] + 0.587 * full[i + 1] + 0.114 * full[i + 2];
  };
  const variance = (arr) => {
    if (!arr.length) return 0;
    const m = arr.reduce((a, b) => a + b, 0) / arr.length;
    let v = 0;
    for (let i = 0; i < arr.length; i++) { const d = arr[i] - m; v += d * d; }
    return v / arr.length;
  };

  // A. Moiré — 64×64 patch centred on the nose/forehead region of the crop.
  const P = 64;
  const ox = Math.floor((S - P) / 2);
  const oy = Math.floor((S - P) / 2);
  const gray = new Float32Array(P * P);
  let sum = 0;
  for (let y = 0; y < P; y++) {
    for (let x = 0; x < P; x++) {
      const g = grayAt(ox + x, oy + y);
      gray[y * P + x] = g;
      sum += g;
    }
  }
  const meanB = sum / (P * P) || 1;
  const rowDiffs = [];
  const colDiffs = [];
  for (let y = 0; y < P; y++) {
    for (let x = 1; x < P; x++) rowDiffs.push(gray[y * P + x] - gray[y * P + x - 1]);
  }
  for (let y = 1; y < P; y++) {
    for (let x = 0; x < P; x++) colDiffs.push(gray[y * P + x] - gray[(y - 1) * P + x]);
  }
  const moire_score = ((variance(rowDiffs) + variance(colDiffs)) / 2) / meanB;

  // B. Backlight uniformity — brightness at 4 corners + centre of the crop.
  const region = (cx, cy) => {
    let s = 0;
    let n = 0;
    const r = 12;
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        if (x >= 0 && x < S && y >= 0 && y < S) { s += grayAt(x, y); n += 1; }
      }
    }
    return s / (n || 1);
  };
  const pts = [region(30, 30), region(170, 30), region(30, 170), region(170, 170), region(100, 100)];
  const m = pts.reduce((a, b) => a + b, 0) / pts.length;
  const sd = Math.sqrt(variance(pts));
  const brightness_cv = m > 0 ? sd / m : 0;

  return {
    moire_score: Math.round(moire_score * 1000) / 1000,
    brightness_cv: Math.round(brightness_cv * 1000) / 1000,
  };
};

/**
 * Crop the detected face to a 200×200 JPEG thumbnail AND compute the
 * screen/display anti-spoofing indicators on that same crop. Shared by
 * captureFaceDescriptor and the liveness final-frame fast path so a capture
 * never costs more than the single detection that produced `box`.
 */
const _cropAndIndicators = (videoEl, box, score, descriptor) => {
  const pad = 0.25;
  const sx = Math.max(0, box.x - box.width * pad);
  const sy = Math.max(0, box.y - box.height * pad);
  const sw = Math.min(videoEl.videoWidth - sx, box.width * (1 + 2 * pad));
  const sh = Math.min(videoEl.videoHeight - sy, box.height * (1 + 2 * pad));
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(videoEl, sx, sy, sw, sh, 0, 0, 200, 200);
  const photoDataUrl = canvas.toDataURL('image/jpeg', 0.75);
  const screen_indicators = _computeScreenIndicators(ctx);
  return { descriptor, photoDataUrl, score, screen_indicators };
};

// ── Liveness helpers (anti-spoofing) — pure JS, no extra model ──
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const _mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
const _stdDev = (arr) => {
  if (!arr.length) return 0;
  const m = _mean(arr);
  let v = 0;
  for (let i = 0; i < arr.length; i++) { const d = arr[i] - m; v += d * d; }
  return Math.sqrt(v / arr.length);
};
const _meanAbsDiff = (a, b) => {
  if (!a || !b || a.length !== b.length) return 0;
  let s = 0;
  for (let i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
  return s / a.length;
};

// Sample an NxN grayscale patch from the live video centred at (cx, cy) in
// intrinsic video-pixel coordinates (the same space face-api returns).
const _sampleGrayPatch = (videoEl, cx, cy, size) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const half = size / 2;
  const sx = Math.max(0, Math.min((videoEl.videoWidth || size) - size, cx - half));
  const sy = Math.max(0, Math.min((videoEl.videoHeight || size) - size, cy - half));
  ctx.drawImage(videoEl, sx, sy, size, size, 0, 0, size, size);
  const { data } = ctx.getImageData(0, 0, size, size);
  const gray = new Float32Array(size * size);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return gray;
};

/** Pick a random liveness challenge — 50% a head-turn (left/right), else front. */
export const randomLivenessChallenge = () => {
  const r = Math.random();
  if (r < 0.5) return null;          // passive (motion + texture only)
  return r < 0.75 ? 'left' : 'right';
};

/**
 * Active liveness capture. Samples 8 frames (~2s), measuring:
 *  • motion_variance — mean abs frame-to-frame diff of a 64×64 nose patch
 *    (a real handheld face has micro-motion; a static photo is ~still).
 *  • texture_std_dev — grayscale std-dev of a 100×100 face patch
 *    (real skin has texture; a screen photo is flat/uniform).
 *  • challenge_passed — when a random head-turn challenge is set, whether the
 *    detected yaw matched the requested direction.
 *
 * SPEED: frames 1–7 use a fast inputSize=128 detection (motion/yaw only). The
 * FINAL frame runs inputSize=224 WITH the descriptor — so the descriptor, the
 * liveness signals and the photo crop all come from ONE detection pass (no
 * separate captureFaceDescriptor call). ~2s total instead of ~8s.
 * Returns { descriptor, photoDataUrl, score, liveness } or { error }.
 * The raw signals are sent to the server, which is the SOLE accept/reject authority.
 */
export const captureFaceWithLiveness = async (videoEl, { challenge = null, frames = 8, intervalMs = 60 } = {}) => {
  if (!modelsLoaded) await loadFaceModels();
  const motionSamples = [];
  const poseSamples = [];
  let prevPatch = null;
  let lastBox = null;
  let frameCount = 0;
  let finalCap = null;   // descriptor + photo + screen indicators from the final frame

  for (let i = 0; i < frames; i++) {
    if (!videoEl || videoEl.readyState < 2) { await _sleep(intervalMs); continue; }
    const isFinal = i === frames - 1;
    try {
      // Frames 1..7 → fast 128px detection (bbox + landmarks only).
      // Final frame → full 224px detection WITH descriptor (one pass for all).
      const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: isFinal ? 224 : 128, scoreThreshold: 0.4 });
      const builder = faceapi.detectSingleFace(videoEl, opts).withFaceLandmarks();
      const det = isFinal ? await builder.withFaceDescriptor() : await builder;
      if (det && det.detection.score >= 0.4) {
        frameCount += 1;
        const nose = det.landmarks.positions[30];
        lastBox = det.detection.box;
        const patch = _sampleGrayPatch(videoEl, nose.x, nose.y, 64);
        if (prevPatch) motionSamples.push(_meanAbsDiff(patch, prevPatch));
        prevPatch = patch;
        poseSamples.push(classifyPose(computeHeadYaw(det.landmarks)));
        if (isFinal) {
          finalCap = _cropAndIndicators(videoEl, det.detection.box, det.detection.score, Array.from(det.descriptor));
        }
      }
    } catch { /* ignore a single bad frame */ }
    if (i < frames - 1) await _sleep(intervalMs);   // no trailing sleep after the final frame
  }

  // Fallback: the final frame failed to detect a face — do one clean capture.
  if (!finalCap) {
    const cap = await captureFaceDescriptor(videoEl);
    if (cap.error) return { error: cap.error };
    finalCap = cap;
  }

  const motion_variance = _mean(motionSamples);
  let texture_std_dev = 0;
  if (lastBox) {
    const cx = lastBox.x + lastBox.width / 2;
    const cy = lastBox.y + lastBox.height / 2;
    texture_std_dev = _stdDev(_sampleGrayPatch(videoEl, cx, cy, 100));
  }

  let challenge_passed = true;
  if (challenge === 'left' || challenge === 'right') {
    challenge_passed = poseSamples.filter((p) => p === challenge).length >= 2;
  }

  return {
    ...finalCap,
    liveness: {
      motion_variance: Math.round(motion_variance * 1000) / 1000,
      texture_std_dev: Math.round(texture_std_dev * 1000) / 1000,
      challenge_passed,
      challenge_type: challenge || 'front',
      frame_count: frameCount,
      screen_indicators: finalCap.screen_indicators || null,
    },
  };
};

/**
 * Start the live camera stream. The `frameRate: { min: 10 }` constraint makes the
 * browser refuse a static-image source (frameRate 0); we also assert the acquired
 * track is actually 'live' so a stopped/ended track can't be used for capture.
 */
export const startVideoStream = async (videoEl, facingMode = 'user') => {
  // On Capacitor/Android, explicitly request camera permission through the
  // native plugin BEFORE calling getUserMedia. The WebView's getUserMedia
  // silently gets NotAllowedError even when Android system permission is
  // granted, because the Capacitor WebView needs its own permission grant.
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Camera } = await import('@capacitor/camera');
      const status = await Camera.checkPermissions();
      if (status.camera !== 'granted') {
        const req = await Camera.requestPermissions({ permissions: ['camera'] });
        if (req.camera !== 'granted') {
          throw new Error('Camera permission denied by user.');
        }
      }
    }
  } catch (e) {
    // If the plugin import fails (web/desktop), continue — getUserMedia handles it
    if (e?.message?.includes('permission denied') || e?.message?.includes('denied by user')) {
      throw e;
    }
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode, width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { min: 10 } },
    audio: false,
  });
  const liveTrack = stream.getVideoTracks().some((t) => t.readyState === 'live');
  if (!liveTrack) {
    stopVideoStream(stream);
    throw new Error('Camera stream is not live — capture refused.');
  }
  videoEl.srcObject = stream;
  await videoEl.play();
  return stream;
};

export const stopVideoStream = (stream) => {
  if (stream) stream.getTracks().forEach((t) => t.stop());
};

/**
 * Get the device's current GPS position.
 *
 * Fix: Xiaomi MIUI + Tecno HiOS GPS accuracy bug.
 * Root cause: `maximumAge: 60000` causes these OEMs to return a stale
 * Network Location Provider fix (±300–500m) instead of firing the GPS
 * hardware. The browser's `enableHighAccuracy: true` is effectively ignored
 * because a "fresh" (within 60s) NLP fix is already in the cache.
 *
 * Strategy:
 *  1. `maximumAge: 0` — never accept a cached position, always request fresh
 *  2. `watchPosition` loop — keep sampling until accuracy ≤ TARGET_ACCURACY_M
 *     or WATCH_TIMEOUT_MS elapses (whichever comes first)
 *  3. On timeout, return best fix seen so far so the user is NEVER blocked —
 *     `accuracy` is reported so the backend can apply the accuracy bypass
 *
 * Typical timings:
 *   Good conditions (open sky):      3–8s,  accuracy ±5–15m
 *   Xiaomi/Tecno indoors/shade:      8–15s, accuracy ±20–80m
 *   Xiaomi/Tecno worst case:         15s timeout, accuracy ±100–300m (fallback)
 */
const _GPS_TARGET_ACCURACY_M = 100;   // accept fix when accuracy ≤ this
const _GPS_WATCH_TIMEOUT_MS  = 15000; // max wait for a good fix

export const getCurrentPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return resolve(null);

    let bestFix   = null;   // best coords seen so far
    let watchId   = null;
    let settled   = false;

    const _finish = async (coords) => {
      if (settled) return;
      // Mock-location detection (existing Fix-6 logic, unchanged)
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
          const { Geolocation } = await import('@capacitor/geolocation');
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true, timeout: 5000, maximumAge: 0,
          });
          if (pos?.mocked === true || pos?.coords?.altitudeAccuracy === -1) {
            if (watchId !== null) navigator.geolocation.clearWatch(watchId);
            settled = true;
            reject(new Error('MOCK_LOCATION_DETECTED'));
            return;
          }
        }
      } catch (e) {
        if (e?.message === 'MOCK_LOCATION_DETECTED') {
          if (watchId !== null) navigator.geolocation.clearWatch(watchId);
          settled = true;
          reject(e);
          return;
        }
        // Native plugin missing or web/iOS — fail open
      }
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      settled = true;
      resolve({
        lat: coords.latitude,
        lng: coords.longitude,
        accuracy: coords.accuracy,
        is_mock_location: false,
      });
    };

    // Fallback: return best fix seen when timeout expires (accuracy may be poor
    // but accuracy_m is reported so the backend bypass can still allow the punch)
    const fallbackTimer = setTimeout(() => {
      if (settled) return;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      settled = true;
      if (bestFix) {
        resolve({
          lat: bestFix.latitude,
          lng: bestFix.longitude,
          accuracy: bestFix.accuracy,
          is_mock_location: false,
        });
      } else {
        resolve(null);  // no fix at all in 15s
      }
    }, _GPS_WATCH_TIMEOUT_MS);

    // watchPosition with maximumAge: 0 forces a fresh reading every callback —
    // this bypasses the stale NLP cache on Xiaomi/Tecno.
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const c = pos.coords;
        if (!bestFix || c.accuracy < bestFix.accuracy) bestFix = c;
        if (c.accuracy <= _GPS_TARGET_ACCURACY_M) {
          clearTimeout(fallbackTimer);
          _finish(c);
        }
        // Otherwise keep watching — a better fix may arrive
      },
      () => {
        // GPS permission denied or hardware error — give up immediately
        if (!settled && !bestFix) {
          clearTimeout(fallbackTimer);
          if (watchId !== null) navigator.geolocation.clearWatch(watchId);
          settled = true;
          resolve(null);
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: _GPS_WATCH_TIMEOUT_MS },
    );
  });
